import JobSearchFiltersForm, {
  JobSearchFiltersFormHandle,
} from '@/modules/JobSearchFilters/components/JobSearchFiltersForm/JobSearchFiltersForm';
import { ILocation, ISchedulerLocation } from '@/services/types';
import {
  DateSearch,
  IRootSpecialityV2,
  ISpeciality,
  ISubSpeciality,
  JobGrade,
  JobSearchGrade,
  JobSearchGrades,
  JobSearchRootSpecialitiesV2,
  JobSearchRootSpeciality,
  JobSearchSpecialities,
  JobSearchSpeciality,
  JobSearchSubSpecialities,
  JobSearchSubSpeciality,
  JobSearchWebCriteria,
  MinPayRateUnit,
  WorkType,
} from '@/types/jobs';
import { isValidDate } from '@/utils/dateHelper';
import { pushGtmEvent } from '@/utils/gtm';
import { generateJobListingsUrlObject } from '@/utils/jobListingHelper';
import { generateLocationNameFromLocationString } from '@/utils/jobLocationsHelper';
import {
  DEFAULT_MIN_PAY_RATE_UNIT,
  DEFAULT_RADIUS,
  getGradeOptions,
  getRootSpecialityOptions,
  getSpecialityOptions,
  getSubSpecialityOptions,
} from '@/utils/jobSearchBoxOptions';
import { setGradeCookie, setLocationCookie, setSpecialityCookie } from '@/utils/jobSearchCookies';
import { MixpanelEvents, useAnalytics } from '@/utils/useAnalytics';
import {
  getJobGrades,
  getRootSpecialtiesV2,
  getSpecialities,
  getSubSpecialitiesV2,
} from '@/utils/useRequest';
import { useRouter } from 'next/router';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

export type JobSearchFiltersProps = {
  query?: JobSearchWebCriteria;
  onOpenChange?: (isOpen: boolean) => void;
};

type GradeFilter = JobSearchGrade | undefined;
type SpecialityFilter = JobSearchSpeciality | undefined;
type RootSpecialityFilter = JobSearchRootSpeciality | undefined;
type SubSpecialityFilter = JobSearchSubSpeciality | undefined;
type KeywordsFilter = string | undefined;
type WorkTypeFilter = WorkType | undefined;
type DateFilter = DateSearch;
type RadiusFilter = string | undefined;
type LocationCoordinateFilter = ILocation | ISchedulerLocation | null;
type MinPayRateFilter = string | null;
type MinPayRateUnitFilter = MinPayRateUnit;

type AppliedFilter = MixpanelEvents['Apply Job Search Filter']['applied_filter'];

function getGtmFilterValue(appliedFilter: AppliedFilter): string {
  if (appliedFilter.filter_type === 'location') {
    return appliedFilter.filter_value.locationName || appliedFilter.filter_value.location || '';
  }
  if (appliedFilter.filter_type === 'minPayRate') {
    return appliedFilter.filter_value.minPayRate ?? '';
  }
  if (appliedFilter.filter_type === 'clear') {
    return appliedFilter.filter_value;
  }
  return appliedFilter.filter_value ?? '';
}

const JobSearchFilters = forwardRef<JobSearchFiltersFormHandle, JobSearchFiltersProps>(
  ({ query, onOpenChange }, ref) => {
    const router = useRouter();
    const track = useAnalytics();
    // filter changes are queued here (keyed by filter_type, so only the latest value per
    // field survives) and only actually sent to analytics when the Search button is clicked
    const pendingTrackEventsRef = useRef<Map<string, AppliedFilter>>(new Map());

    // grade and speciality lists
    const [grades, setGrades] = useState<JobSearchGrades | null>([
      { key: 'Loading', value: 'Loading' },
    ]);
    const [specialities, setSpecialities] = useState<JobSearchSpecialities>({});
    const [rootSpecialities, setRootSpecialities] = useState<JobSearchRootSpecialitiesV2>({});
    const [subSpecialities, setSubSpecialities] = useState<JobSearchSubSpecialities>({});

    // form state
    const [date, setDate] = useState<DateFilter>({
      startDate: query?.startDate ?? null,
      endDate: query?.endDate ?? null,
    });
    const [grade, setGrade] = useState<GradeFilter>(query?.grade);

    // TODO: update the below speciality states when ready
    const [speciality, setSpeciality] = useState<SpecialityFilter>(query?.speciality);
    const [rootSpeciality, setRootSpeciality] = useState<RootSpecialityFilter>(
      query?.rootSpeciality,
    );
    const [subspeciality, setSubspeciality] = useState<SubSpecialityFilter>(query?.subspeciality);

    const [keywords, setKeywords] = useState<KeywordsFilter>(query?.keywords);
    const [workType, setWorkType] = useState<WorkTypeFilter>(query?.workType);
    const [radius, setRadius] = useState<RadiusFilter>(query?.radius);
    const [locationCoordinate, setLocationCoordinate] = useState<LocationCoordinateFilter>({
      name: query?.locationName
        ? query?.locationName
        : generateLocationNameFromLocationString(query?.location),
      position: query?.lat && query?.lon ? { lat: +query.lat, lon: +query.lon } : undefined,
      searchLocations: query?.location ? query.location?.split(';') : undefined,
    });
    const [minPayRate, setMinPayRate] = useState<MinPayRateFilter>(query?.minPayRate ?? null);
    const [minPayRateUnit, setMinPayRateUnit] = useState<MinPayRateUnitFilter>(
      query?.minPayRateUnit ?? DEFAULT_MIN_PAY_RATE_UNIT,
    );
    // end of form state

    const isEndDateDisabled = workType === 'Permanent';
    const isRadiusDisabled = !(
      (locationCoordinate as ILocation)?.position?.lon &&
      (locationCoordinate as ILocation)?.position?.lat
    );

    const getSpecialityByGrade = async (
      newGrade: JobSearchGrade | undefined,
    ): Promise<JobSearchSpeciality | undefined> => {
      if (!newGrade || newGrade === 'none') {
        return undefined;
      }

      const filteredGrade = newGrade as JobGrade;
      let specialitiesPerGrade: Array<ISpeciality>;

      // fetch allowed specialty values per selected grade if not fetched yet
      if (specialities[filteredGrade]) {
        specialitiesPerGrade = specialities[filteredGrade];
      } else {
        const result = await getSpecialities(filteredGrade);
        if ('error' in result) {
          // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
          return undefined;
        } else {
          setSpecialities({
            ...specialities,
            [filteredGrade]: result.response.data,
          });
          specialitiesPerGrade = result.response.data;
        }
      }

      // reset selected speciality if it is not found in the allowed specialty values
      if (specialitiesPerGrade && specialitiesPerGrade.length > 0) {
        const specialitiesSet: Set<JobSearchSpeciality | undefined> = new Set(
          specialitiesPerGrade.map(i => i.value),
        );
        return specialitiesSet.has(speciality) ? speciality : undefined;
      }

      return undefined;
    };

    const getRootSpecialityByGrade = async (
      newGrade: JobSearchGrade | undefined,
    ): Promise<JobSearchRootSpeciality | undefined> => {
      if (!newGrade || newGrade === 'none') {
        return undefined;
      }

      const filteredGrade = newGrade as JobGrade;
      let rootSpecialitiesPerGrade: Array<IRootSpecialityV2>;

      // fetch allowed specialty values per selected grade if not fetched yet
      if (rootSpecialities[filteredGrade]) {
        rootSpecialitiesPerGrade = rootSpecialities[filteredGrade];
      } else {
        const result = await getRootSpecialtiesV2(filteredGrade);
        if ('error' in result) {
          // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
          return undefined;
        } else {
          setRootSpecialities({
            ...rootSpecialities,
            [filteredGrade]: result.response.data,
          });
          rootSpecialitiesPerGrade = result.response.data;
        }
      }

      // reset selected speciality if it is not found in the allowed specialty values
      if (rootSpecialitiesPerGrade && rootSpecialitiesPerGrade.length > 0) {
        const rootSpecialitiesSet: Set<JobSearchRootSpeciality | undefined> = new Set(
          rootSpecialitiesPerGrade.map(i => i.value),
        );
        return rootSpecialitiesSet.has(rootSpeciality) ? rootSpeciality : undefined;
      }

      return undefined;
    };

    const getSubSpecialityByRootSpeciality = async (
      newGrade: JobSearchGrade | undefined,
      rootSpeciality?: JobSearchRootSpeciality,
    ): Promise<JobSearchSubSpeciality | undefined> => {
      if (!newGrade || newGrade === 'none') {
        return undefined;
      }
      if (!rootSpeciality || rootSpeciality === 'none') {
        return undefined;
      }

      let subSpecialitiesPerRootSpeciality: Array<ISubSpeciality>;
      const result = await getSubSpecialitiesV2(newGrade, rootSpeciality);

      if ('error' in result) {
        // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
        return undefined;
      } else {
        setSubSpecialities({
          ...subSpecialities,
          [rootSpeciality]: result.response.data,
        });
        subSpecialitiesPerRootSpeciality = result.response.data;
      }

      // reset selected speciality if it is not found in the allowed specialty values
      if (subSpecialitiesPerRootSpeciality && subSpecialitiesPerRootSpeciality.length > 0) {
        const subSpecialitiesSet: Set<JobSearchSubSpeciality | undefined> = new Set(
          subSpecialitiesPerRootSpeciality.map(i => i.value),
        );
        return subSpecialitiesSet.has(subspeciality) ? subspeciality : undefined;
      }

      return undefined;
    };

    // Loads the root/sub-speciality option lists for a grade (and root speciality) if they
    // aren't already cached, without the "reset to undefined if not allowed" validation that
    // getRootSpecialityByGrade/getSubSpecialityByRootSpeciality do - that validation reads the
    // *current* rootSpeciality/subspeciality state, which during a query resync is still the
    // previous page's value and hasn't been replaced with the query's yet.
    const ensureSpecialityOptionsLoadedForGrade = async (
      newGrade: JobSearchGrade | undefined,
      newRootSpeciality: JobSearchRootSpeciality | undefined,
    ) => {
      if (newGrade && newGrade !== 'none' && !rootSpecialities[newGrade as JobGrade]) {
        const result = await getRootSpecialtiesV2(newGrade as JobGrade);
        if (!('error' in result)) {
          setRootSpecialities(current => ({
            ...current,
            [newGrade as JobGrade]: result.response.data,
          }));
        }
      }

      if (
        newGrade &&
        newGrade !== 'none' &&
        newRootSpeciality &&
        newRootSpeciality !== 'none' &&
        !subSpecialities[newRootSpeciality]
      ) {
        const result = await getSubSpecialitiesV2(newGrade, newRootSpeciality);
        if (!('error' in result)) {
          setSubSpecialities(current => ({
            ...current,
            [newRootSpeciality]: result.response.data,
          }));
        }
      }
    };

    // Only the actual filter *values* should trigger a resync - `query` is a fresh object on
    // every navigation (including pagination, which leaves filters unchanged), so keying the
    // effect on `query` itself would wipe un-submitted draft state (e.g. a locally-picked
    // minPayRateUnit, or a date typed but not yet submitted) on every page click.
    const resyncKey = useMemo(
      () =>
        JSON.stringify([
          query?.grade,
          query?.speciality,
          query?.rootSpeciality,
          query?.subspeciality,
          query?.keywords,
          query?.workType,
          query?.radius,
          query?.startDate,
          query?.endDate,
          query?.locationName,
          query?.location,
          query?.lat,
          query?.lon,
          query?.minPayRate,
          query?.minPayRateUnit,
        ]),
      [
        query?.grade,
        query?.speciality,
        query?.rootSpeciality,
        query?.subspeciality,
        query?.keywords,
        query?.workType,
        query?.radius,
        query?.startDate,
        query?.endDate,
        query?.locationName,
        query?.location,
        query?.lat,
        query?.lon,
        query?.minPayRate,
        query?.minPayRateUnit,
      ],
    );

    // Resync all filter state from `query` whenever the filter values actually change - e.g.
    // after a client-side navigation to a different canonical listing URL. The
    // `useState(query?.x)` initializers above only seed state on the initial mount, so without
    // this the filter widgets would keep showing the previous page's selections after
    // navigating via a Link (breadcrumbs, "Explore similar searches", etc.) rather than
    // reflecting the new page's actual filters.
    useEffect(() => {
      setGrade(query?.grade);
      setSpeciality(query?.speciality);
      setRootSpeciality(query?.rootSpeciality);
      setSubspeciality(query?.subspeciality);
      setKeywords(query?.keywords);
      setWorkType(query?.workType);
      setRadius(query?.radius);
      setDate({ startDate: query?.startDate ?? null, endDate: query?.endDate ?? null });
      setLocationCoordinate({
        name: query?.locationName
          ? query?.locationName
          : generateLocationNameFromLocationString(query?.location),
        position: query?.lat && query?.lon ? { lat: +query.lat, lon: +query.lon } : undefined,
        searchLocations: query?.location ? query.location?.split(';') : undefined,
      });
      setMinPayRate(query?.minPayRate ?? null);
      setMinPayRateUnit(query?.minPayRateUnit ?? DEFAULT_MIN_PAY_RATE_UNIT);
      ensureSpecialityOptionsLoadedForGrade(query?.grade, query?.rootSpeciality);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resyncKey]);

    // Fetch the list of grades and specialties on initial load
    useEffect(() => {
      const getJobGradesAndSpecialtiesV1 = async () => {
        try {
          const grades = await getJobGrades();
          setGrades(grades);
          const speciality = await getSpecialityByGrade(query?.grade);
          setSpeciality(speciality);
        } catch (error) {
          // error already reported by makeRequestAndNoticeErrors
          return null;
        }
      };

      const getJobGradesAndRootSpecialtiesAndSubSpecialitiesV2 = async () => {
        try {
          const grades = await getJobGrades();
          setGrades(grades);
          const rootSpeciality = await getRootSpecialityByGrade(query?.grade);
          setRootSpeciality(rootSpeciality);
          const subspeciality = await getSubSpecialityByRootSpeciality(
            query?.grade,
            rootSpeciality,
          );
          setSubspeciality(subspeciality);
        } catch (error) {
          // error already reported by makeRequestAndNoticeErrors
          return null;
        }
      };

      getJobGradesAndRootSpecialtiesAndSubSpecialitiesV2();
    }, []);

    function handleChange(search: JobSearchWebCriteria) {
      router.push(generateJobListingsUrlObject(search));
    }

    function handleClearAll() {
      setRadius(undefined);
      setWorkType(undefined);
      setKeywords(undefined);
      setGrade(undefined);
      setSpeciality(undefined);
      setDate({ startDate: null, endDate: null });
      setLocationCoordinate(null);
      setMinPayRate(null);
      setMinPayRateUnit(DEFAULT_MIN_PAY_RATE_UNIT);
      pendingTrackEventsRef.current.clear();
      pushGtmEvent({
        event: 'filter_clear_all',
        element: 'Clear All',
      });
      track('Apply Job Search Filter', {
        applied_filter: {
          filter_type: 'clear',
          filter_value: 'all',
        },
        search: {},
      });
      handleChange({});
    }

    function getGradeOpts() {
      return getGradeOptions(grades);
    }

    function getSpecialityOpts() {
      return getSpecialityOptions(grade, specialities);
    }

    function getRootSpecialityOpts() {
      return getRootSpecialityOptions(grade, rootSpecialities);
    }
    function getSubSpecialityOpts() {
      return getSubSpecialityOptions(rootSpeciality, subSpecialities);
    }

    function formatLocation(newLocationCoordinate: LocationCoordinateFilter): {
      location: string | undefined;
      locationName: string | undefined;
      lon: string | undefined;
      lat: string | undefined;
    } {
      return {
        location: (newLocationCoordinate as ISchedulerLocation)?.searchLocations?.join(';'),
        locationName: newLocationCoordinate?.name,
        lon: (newLocationCoordinate as ILocation)?.position?.lon.toString(),
        lat: (newLocationCoordinate as ILocation)?.position?.lat.toString(),
      };
    }

    function formatPayRate(newMinPayRate: MinPayRateFilter): {
      minPayRate: string | null;
      minPayRateUnit: MinPayRateUnit | undefined;
    } {
      return {
        minPayRate: newMinPayRate,
        minPayRateUnit: newMinPayRate ? minPayRateUnit : undefined,
      };
    }

    function generateSearchObject(
      changedProps: Partial<JobSearchWebCriteria>,
    ): JobSearchWebCriteria {
      return {
        ...date,
        grade,
        speciality,
        rootSpeciality,
        subspeciality,
        keywords,
        workType,
        ...formatPayRate(minPayRate),
        ...formatLocation(locationCoordinate),
        radius,
        sortBy: query?.sortBy,
        page: 1, // always set page back to 1 on filter change
        ...changedProps,
      };
    }

    function queueTrackEvent(appliedFilter: AppliedFilter) {
      pendingTrackEventsRef.current.set(appliedFilter.filter_type, appliedFilter);
    }

    function handleSearch() {
      const search = generateSearchObject({});
      pendingTrackEventsRef.current.forEach(appliedFilter => {
        track('Apply Job Search Filter', {
          applied_filter: appliedFilter,
          search,
        });
        // workType has its own dedicated filter_work_type_tab event, fired immediately on tab click
        if (appliedFilter.filter_type !== 'workType') {
          pushGtmEvent({
            event: 'filter_apply',
            element: 'Filter',
            filter_name: appliedFilter.filter_type,
            filter_value: getGtmFilterValue(appliedFilter),
          });
        }
      });
      pendingTrackEventsRef.current.clear();
      handleChange(search);
    }

    function handleDateChange(newDate: DateFilter) {
      // only handle date changes when the date is valid to allow a possibility of typing dates in manually
      setDate(newDate);
      if (
        date.startDate === newDate.startDate &&
        date.endDate !== newDate.endDate && // end date changed and
        isValidDate(newDate.endDate, 'YYYY-MM-DD') // and it is valid
      ) {
        queueTrackEvent({ filter_type: 'endDate', filter_value: newDate.endDate });
      }
      if (
        date.startDate !== newDate.startDate && // start date changed
        date.endDate === newDate.endDate &&
        isValidDate(newDate.startDate, 'YYYY-MM-DD') // and it is valid
      ) {
        queueTrackEvent({ filter_type: 'startDate', filter_value: newDate.startDate });
      }
    }

    async function handleGradeChange(newGrade: GradeFilter) {
      // - fetch new list if it is not fetched yet
      // - keep the selected specialty if on the new list or reset

      // Change the RootSpeciality
      const newRootSpeciality = await getRootSpecialityByGrade(newGrade);
      setRootSpeciality(newRootSpeciality);
      const newSubspeciality = await getSubSpecialityByRootSpeciality(newGrade, newRootSpeciality);
      setSubspeciality(newSubspeciality);

      // The legacy (v1) speciality field isn't exposed by the current filter UI - it can only
      // arrive via an incoming query param, e.g. an old pre-redesign URL. Without this, changing
      // grade here would leave a stale v1 speciality behind (invisible in the UI) that still gets
      // encoded into the resulting listing URL, same as handleClearAll already does for it. Only
      // run the (network-fetching) validation when there's actually a v1 speciality to check -
      // skip it in the common case where one was never present, so changing grade doesn't fire
      // an unnecessary v1 specialities request every time.
      if (speciality) {
        const newSpeciality = await getSpecialityByGrade(newGrade);
        setSpeciality(newSpeciality);
      }

      setGrade(newGrade);

      queueTrackEvent({ filter_type: 'grade', filter_value: newGrade });
      if (newGrade) {
        setGradeCookie(newGrade);
      }
    }

    async function handleRootSpecialityChange(newRootSpeciality: RootSpecialityFilter) {
      // side effects: subspecialty
      // - fetch new list if it is not fetched yet
      // - keep the selected subspecialty if on the new list or reset
      const newSubSpeciality = await getSubSpecialityByRootSpeciality(grade, newRootSpeciality);
      setRootSpeciality(newRootSpeciality);
      setSubspeciality(newSubSpeciality);
      queueTrackEvent({ filter_type: 'rootSpeciality', filter_value: newRootSpeciality });
    }

    function handleSpecialtyChange(newSpeciality: SpecialityFilter) {
      setSpeciality(newSpeciality);
      queueTrackEvent({ filter_type: 'speciality', filter_value: newSpeciality });
      if (newSpeciality) {
        setSpecialityCookie(newSpeciality);
      }
    }

    function handleSubSpecialityChange(newSubSpeciality: SubSpecialityFilter) {
      setSubspeciality(newSubSpeciality);
      queueTrackEvent({ filter_type: 'subspeciality', filter_value: newSubSpeciality });
    }

    function handleKeywordsChange(newKeywords: KeywordsFilter) {
      setKeywords(newKeywords);
      queueTrackEvent({ filter_type: 'keywords', filter_value: newKeywords });
    }

    function handleWorkTypeChange(newWorkType: WorkTypeFilter) {
      // side effects: end date
      // - reset end date if Permanent is selected
      setWorkType(newWorkType);
      if (newWorkType === 'Permanent') {
        setDate({
          startDate: date.startDate,
          endDate: null,
        });
      }
      queueTrackEvent({ filter_type: 'workType', filter_value: newWorkType });
      pushGtmEvent({
        event: 'filter_work_type_tab',
        element: 'Work Type Tab',
        tab_value: newWorkType ?? 'All',
      });
    }

    function handleLocationChange(newLocationCoordinate: LocationCoordinateFilter) {
      // side effects: radius
      // - set to default or remove depending on type of location
      const newLocation = formatLocation(newLocationCoordinate);
      const newRadius = newLocation?.lat && newLocation?.lon ? radius ?? DEFAULT_RADIUS : undefined;
      setLocationCoordinate(newLocationCoordinate);
      setRadius(newRadius);
      queueTrackEvent({ filter_type: 'location', filter_value: newLocation });
      if (newLocationCoordinate) {
        setLocationCookie(newLocationCoordinate);
      }
    }

    function handleRadiusChange(newRadius: RadiusFilter) {
      setRadius(newRadius);
      queueTrackEvent({ filter_type: 'radius', filter_value: newRadius });
    }

    function handleMinPayRateChange(newMinPayRate: MinPayRateFilter) {
      // side effects: minPayRateUnit
      // - set to default or remove
      setMinPayRate(newMinPayRate);
      const newPayRate = formatPayRate(newMinPayRate);
      queueTrackEvent({ filter_type: 'minPayRate', filter_value: newPayRate });
    }

    return (
      <JobSearchFiltersForm
        ref={ref}
        onOpenChange={onOpenChange}
        date={date}
        onDateChange={handleDateChange}
        grade={grade}
        onGradeChange={grade => {
          handleGradeChange(grade)
            .then(() => null)
            .catch(() => null); // the error get reported by makeRequestAndNoticeErrors, and we don't show any notification to the user
        }}
        speciality={speciality}
        onSpecialityChange={handleSpecialtyChange}
        keyword={keywords}
        onKeywordChange={handleKeywordsChange}
        workType={workType}
        onWorkTypeChange={handleWorkTypeChange}
        locationCoordinate={locationCoordinate}
        onLocationCoordinateChange={handleLocationChange}
        radius={radius}
        onRadiusChange={handleRadiusChange}
        minPayRate={minPayRate}
        onMinPayRateChange={handleMinPayRateChange}
        minPayRateUnit={minPayRateUnit}
        onMinPayRateUnitChange={setMinPayRateUnit}
        isEndDateDisabled={isEndDateDisabled}
        isRadiusDisabled={isRadiusDisabled}
        onClear={handleClearAll}
        onSearch={handleSearch}
        getGradeOptions={getGradeOpts}
        getSpecialityOptions={getSpecialityOpts}
        rootSpeciality={rootSpeciality}
        onRootSpecialityChange={rootSpeciality => {
          handleRootSpecialityChange(rootSpeciality)
            .then(() => null)
            .catch(() => null); // Same behaviour as grade now we have sub specialities
        }}
        subspeciality={subspeciality}
        onSubSpecialityChange={handleSubSpecialityChange}
        getRootSpecialityOptions={getRootSpecialityOpts}
        getSubSpecialityOptions={getSubSpecialityOpts}
      />
    );
  },
);

JobSearchFilters.displayName = 'JobSearchFilters';

export default JobSearchFilters;
